// src/pages/Categories/CategoryList.tsx

import { Card, Tree, Typography } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '@/api/categories';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getCurrentDeveloperId } from '@/stores/authStore'; // ✅ 导入
import type { ProductCategory } from '@/types/category';

/**
 * 产品类别列表页面
 */
export const CategoryList = () => {
  const developerId = getCurrentDeveloperId(); // ✅ 获取开发者 ID

  // ✅ 查询添加开发者 ID 和 enabled 条件
  const categoryQuery = useQuery({
    queryKey: ['categories', developerId, 3], // ✅ 添加开发者 ID
    queryFn: () => getCategories(3), // 获取三级类别
    enabled: !!developerId, // ✅ 只有登录后才请求
  });

  const categories = categoryQuery.data || [];
  const loading = categoryQuery.isLoading;
  const error = categoryQuery.error;

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <ErrorAlert 
        message="加载失败" 
        description="无法加载产品类别"
        className="mb-4"
      />
    );
  }

  // 转换为 Ant Design Tree 所需的数据结构
  const treeData: DataNode[] = categories.map((category) => 
    convertToTreeData(category)
  );

  /**
   * 递归转换类别为 Tree 数据结构
   */
  function convertToTreeData(category: ProductCategory): DataNode {
    const node: DataNode = {
      title: category.name,
      key: category.id,
      children: [],
    };

    if (category.children && category.children.length > 0) {
      node.children = category.children.map(child => convertToTreeData(child));
    }

    return node;
  }

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-bold mb-6">产品类别</h1>
      
      <Card title="类别树" className="mb-6">
        {treeData.length > 0 ? (
          <Tree
            treeData={treeData}
            defaultExpandAll
            showLine
            showIcon={false}
            className="text-sm"
          />
        ) : (
          <Typography.Text type="secondary">
            暂无类别数据
          </Typography.Text>
        )}
      </Card>
    </div>
  );
};